// ─────────────────────────────────────────────────────────────────
// Glass-i — Stage 6B.8A-3G: Triple selling-price input.
//
// One bordered "field" with three sections separated by tall dividers:
//
//   ┌─────────────────────────────────────────────────────────────┐
//   │ Sell  ┃ 250.00 │ Min  ┃ 230.00 │ Max  ┃ 280.00              │
//   └─────────────────────────────────────────────────────────────┘
//   ≈ ₹23.23 / SQF  (equivalence hint — only shows when the picked
//                    UOM has a known alt; PCS / SET / QTY hide it)
//
// Internally drives THREE AntD Form fields:
//   • salesRate
//   • minSalesRate
//   • maxSalesRate
//
// Backed by a sibling `<Form.Item name="salesRate">` so the existing
// `required` rule still fires. The min / max sections write through
// `form.setFieldValue(...)` so they live on the same form payload
// without needing their own visible Form.Item containers.
// ─────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, useState } from 'react';
import { Form, InputNumber } from 'antd';
import { studioLookups } from '../../itemsApi';
import {
  altUomFor, convertPricePerUnit, formatPrice, isDiscreteUom
} from '../../uomConversions.js';

// Keystroke gate — same policy as the Thickness field. Digits, single
// decimal point, navigation keys, modifier combos. Anything else is
// blocked so the user cannot type letters / negative sign / exponent.
function onPriceKeyDown(e) {
  const allowed = [
    'Backspace', 'Delete', 'Tab', 'Enter', 'Escape',
    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    'Home', 'End'
  ];
  if (allowed.includes(e.key)) return;
  if (e.ctrlKey || e.metaKey) return;
  if (/^[0-9]$/.test(e.key)) return;
  if (e.key === '.' && !e.currentTarget.value.includes('.')) return;
  e.preventDefault();
}

// Strip non-numeric + clamp fractional to 2 chars. Same shape as the
// Thickness parser in CreateGlassModal.
const PRICE_PARSER = (raw) => {
  if (raw == null) return '';
  const cleaned = String(raw).replace(/[^\d.]/g, '');
  const dot = cleaned.indexOf('.');
  if (dot < 0) return cleaned;
  const intPart  = cleaned.slice(0, dot);
  const fracPart = cleaned.slice(dot + 1).replace(/\./g, '').slice(0, 2);
  return fracPart.length ? `${intPart}.${fracPart}` : `${intPart}.`;
};

export default function TripleSalesRateInput({
  // Called by the parent <Form.Item name="salesRate">. We forward
  // through to the Sell section's InputNumber.
  value,
  onChange,
  form,
  disabled
}) {
  const minSales = Form.useWatch('minSalesRate', form);
  const maxSales = Form.useWatch('maxSalesRate', form);
  // Resolve the picked UOM's code so the equivalence hint can show
  // "≈ X / SQF". We watch defaultUomId on the parent form and lazily
  // load the UOM lookup pool once so the lookup is a cheap Map hit.
  const uomId = Form.useWatch('defaultUomId', form);
  const [uomMap, setUomMap] = useState(() => new Map());
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    studioLookups.uoms({})
      .then((rows) => {
        const m = new Map();
        for (const r of (rows || [])) {
          const code = r.uomCode || r.uom_code || r.label || null;
          if (r.id && code) m.set(r.id, String(code).toUpperCase());
        }
        setUomMap(m);
      })
      .catch(() => { /* picker still works; hint just stays hidden */ });
  }, []);
  const uomCode = uomId ? (uomMap.get(uomId) || null) : null;

  const altUom = useMemo(() => altUomFor(uomCode), [uomCode]);
  const hidePriceHint = !uomCode || isDiscreteUom(uomCode) || !altUom;
  const altPrice = useMemo(() => (
    hidePriceHint ? null : convertPricePerUnit(value, uomCode, altUom)
  ), [hidePriceHint, value, uomCode, altUom]);

  const sectionStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    flex: '1 1 0',
    minWidth: 0
  };
  const labelStyle = {
    fontSize: 11,
    color: '#64748b',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    padding: '0 8px',
    whiteSpace: 'nowrap'
  };
  const inputStyle = {
    flex: '1 1 0',
    minWidth: 0,
    border: 'none',
    boxShadow: 'none',
    background: 'transparent'
  };
  const sepStyle = {
    width: 1,
    alignSelf: 'stretch',
    background: '#e2e8f0',
    margin: '4px 0'
  };

  const setMin = (v) => form && form.setFieldValue('minSalesRate', v == null ? null : Number(v));
  const setMax = (v) => form && form.setFieldValue('maxSalesRate', v == null ? null : Number(v));
  const setSell = (v) => onChange && onChange(v == null ? null : Number(v));

  return (
    <div>
      <div
        className="erp-triple-rate"
        style={{
          display: 'flex',
          alignItems: 'stretch',
          width: '100%',
          height: 30,
          border: '1px solid #cbd5e1',
          borderRadius: 6,
          background: '#ffffff',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
          overflow: 'hidden'
        }}
      >
        {/* STD */}
        <span style={sectionStyle}>
          <span style={labelStyle}>STD</span>
          <InputNumber
            value={value}
            onChange={setSell}
            min={0}
            step={1}
            precision={2}
            disabled={disabled}
            placeholder="0.00"
            parser={PRICE_PARSER}
            formatter={(v) => (v == null ? '' : String(v))}
            onKeyDown={onPriceKeyDown}
            controls={false}
            style={inputStyle}
            variant="borderless"
          />
        </span>
        <span style={sepStyle} aria-hidden />

        {/* Min */}
        <span style={sectionStyle}>
          <span style={labelStyle}>Min</span>
          <InputNumber
            value={minSales}
            onChange={setMin}
            min={0}
            step={1}
            precision={2}
            disabled={disabled}
            placeholder="0.00"
            parser={PRICE_PARSER}
            formatter={(v) => (v == null ? '' : String(v))}
            onKeyDown={onPriceKeyDown}
            controls={false}
            style={inputStyle}
            variant="borderless"
          />
        </span>
        <span style={sepStyle} aria-hidden />

        {/* Max */}
        <span style={sectionStyle}>
          <span style={labelStyle}>Max</span>
          <InputNumber
            value={maxSales}
            onChange={setMax}
            min={0}
            step={1}
            precision={2}
            disabled={disabled}
            placeholder="0.00"
            parser={PRICE_PARSER}
            formatter={(v) => (v == null ? '' : String(v))}
            onKeyDown={onPriceKeyDown}
            controls={false}
            style={inputStyle}
            variant="borderless"
          />
        </span>
      </div>

      {/* UOM-equivalence hint. Hidden for discrete UOMs (PCS / SET /
          QTY) and when the picked UOM has no known alt mapping. */}
      {!hidePriceHint && Number.isFinite(altPrice) ? (
        <div style={{
          fontSize: 11,
          color: '#64748b',
          marginTop: 4,
          letterSpacing: 0.1
        }}>
          ≈ ₹{formatPrice(altPrice)} per {altUom}
        </div>
      ) : null}
    </div>
  );
}
TripleSalesRateInput.displayName = 'TripleSalesRateInput';
